// components/Inventario.jsx
import React, { useState, useEffect, useRef } from 'react'; // Asegúrate de importar 'useRef'
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig';
import * as XLSX from 'xlsx'; // Importar la librería para Excel
import { useReactToPrint } from 'react-to-print'; // Importar el hook para imprimir
import CierreMesComponent from './CierreMesComponent';
import './Inventario.css'; // Tus estilos

// --- Diálogo de confirmación estilizado ──────────────────────────────────────
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmText = "Confirmar", cancelText = "Cancelar", loading = false, danger = false, type = 'info' }) => {
    // Sincronización con variables globales de App.css
    let primaryColor = 'var(--primary-color)';
    let gradient = 'var(--primary-gradient)';
    
    if (danger || type === 'danger') {
        primaryColor = 'var(--danger-color)';
        gradient = 'linear-gradient(135deg, #e53e3e 0%, #fc8181 100%)';
    } else if (type === 'success') {
        primaryColor = 'var(--success-color)';
        gradient = 'linear-gradient(135deg, #38a169 0%, #68d391 100%)';
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
            <div className="card" style={{ width: '100%', maxWidth: 420, padding: '2rem', borderTop: `6px solid ${primaryColor}`, animation: 'slideUpFade 0.3s ease' }}>
                <h3 className="card-title" style={{ marginBottom: '1rem', color: 'var(--secondary-color)', fontSize: '1.25rem' }}>
                    {title}
                </h3>
                <p style={{ marginBottom: '2rem', color: '#4a5568', fontSize: '1rem', lineHeight: '1.6' }}>{message}</p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button 
                        onClick={onCancel} 
                        className="btn-secondary" 
                        style={{ padding: '0.6rem 1.4rem' }}
                    >
                        {cancelText}
                    </button>
                    <button 
                        onClick={onConfirm} 
                        disabled={loading} 
                        className={danger || type === 'danger' ? 'btn-danger' : 'btn-primary'}
                        style={{ 
                            padding: '0.6rem 1.6rem',
                            background: gradient 
                        }}
                    >
                        {loading ? 'Procesando...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};
  
const MsgError = ({ msg }) => msg ? (
    <div className="alert alert-danger" style={{ marginBottom: '1rem', width: '100%' }}>{msg}</div>
) : null;

const MsgExito = ({ msg }) => msg ? (
    <div className="alert alert-success" style={{ marginBottom: '1rem', width: '100%' }}>{msg}</div>
) : null;

const Inventario = () => {
    const [vista, setVista] = useState('gestion'); // 'gestion' o 'compra'
    const [insumos, setInsumos] = useState([]);
    const [insumosOriginales, setInsumosOriginales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { auth } = useAuth(); // Usa el hook para obtener el contexto de autenticación
    // --- PASO 1: EL ESTADO DEL RESALTADO AHORA VIVE EN EL PADRE ---
    const [idInsumoActualizado, setIdInsumoActualizado] = useState(null);

    // --- Cargar Insumos ---
    const fetchInsumos = async () => {
        if (!auth?.token) return;
        try {
            // Solo activar loading global si NO tenemos datos previos (primera carga)
            if (insumos.length === 0) setLoading(true);
            
            const response = await fetch(getApiUrl('/insumos'), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al cargar los insumos');
            }
            const data = await response.json();
            setInsumos(data);
            setInsumosOriginales(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInsumos();
    }, [auth?.token]); // Depender del token de autenticación

    // --- Manejadores de eventos ---
    const handleInsumoChange = (id, field, value) => {
        setInsumos(insumos.map(ins => 
            ins.id === id ? { ...ins, [field]: value } : ins
        ));
    };

    const handleGuardarCambios = async (insumo, nota) => {
        try {
            const response = await fetch(getApiUrl(`/insumos/${insumo.id}`), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                body: JSON.stringify({
                    stock_actual: insumo.stock_actual,
                    stock_minimo: insumo.stock_minimo,
                    unidad_medida: insumo.unidad_medida,
                    nota_ajuste: nota 
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al guardar el insumo');
            }
            
            // --- LÓGICA DE RESALTADO Y RECARGA ---
            setIdInsumoActualizado(insumo.id);
            await fetchInsumos();
            setTimeout(() => setIdInsumoActualizado(null), 3000);
            return true; 

        } catch (err) {
            setError(err.message);
            setTimeout(() => setError(null), 5000);
            return false; 
        }
    };


    // --- Renderizado ---
    if (loading) return <div className="loading">Cargando inventario...</div>;
    if (error) return <div className="error-container"><div className="error">Error: {error}</div></div>;

    // --- RENDERIZADO ---
    return (
        <div className="inventario-container">
            <header className="inventario-header">
                <h1>Módulo de Inventario</h1>
                <nav>
                    <button onClick={() => setVista('gestion')} className={vista === 'gestion' ? 'active' : ''}>
                        Gestión de Insumos
                    </button>
                    <button onClick={() => setVista('estado')} className={vista === 'estado' ? 'active' : ''}>
                        Estado de Productos
                    </button>
                    <button onClick={() => setVista('avanzado')} className={vista === 'avanzado' ? 'active' : ''}>
                        Productos y Recetas
                    </button>
                    <button onClick={() => setVista('compra')} className={vista === 'compra' ? 'active' : ''}>
                        Registrar Compra
                    </button>
                    <button onClick={() => setVista('reporte')} className={vista === 'reporte' ? 'active' : ''}>
                        Reporte de Compras
                    </button>
                    <button onClick={() => setVista('cierre')} className={vista === 'cierre' ? 'active' : ''}>
                        Cierre de Mes
                    </button>
                </nav>
            </header>

            {vista === 'gestion' && (
                <GestionInsumos
                    insumos={insumos}
                    insumosOriginales={insumosOriginales}
                    onInsumoChange={handleInsumoChange}
                    onGuardar={handleGuardarCambios} 
                    onInsumoCreado={fetchInsumos}  
                    idInsumoActualizado={idInsumoActualizado}
                />
            )}
            {vista === 'estado' && <EstadoProductos token={auth.token} />}
            {vista === 'compra' && ( <RegistrarCompra insumosList={insumosOriginales} token={auth.token} onCompraSuccess={fetchInsumos} /> )}
            {/* === RENDERIZADO DEL NUEVO COMPONENTE DE REPORTE === */}
            {vista === 'reporte' && <ReporteCompras token={auth.token} />}
            {vista === 'cierre' && <CierreMesComponent token={auth.token} />}
            {vista === 'avanzado' && <GestionCatalogo token={auth.token} />}
        </div>
    );
};

// ====================================================================
// === NUEVO SUB-COMPONENTE PARA GESTIÓN AVANZADA DE CATÁLOGO ===
// ====================================================================
const GestionCatalogo = ({ token }) => {
    const [productos, setProductos] = useState([]);
    const [insumos, setInsumos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Estados para el modal de producto
    const [modalAbierto, setModalAbierto] = useState(false);
    const [productoEditando, setProductoEditando] = useState(null); 

    // Estados para el modal de receta
    const [modalRecetaAbierto, setModalRecetaAbierto] = useState(false);
    const [productoReceta, setProductoReceta] = useState(null);

    // Diálogo de confirmación (NUEVO)
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [mensajeExito, setMensajeExito] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            const [resProductos, resInsumos] = await Promise.all([
                fetch(getApiUrl('/productos'), { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(getApiUrl('/insumos'), { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            if (!resProductos.ok || !resInsumos.ok) throw new Error('Error al cargar datos iniciales');
            const dataProductos = await resProductos.json();
            const dataInsumos = await resInsumos.json();
            setProductos(dataProductos);
            setInsumos(dataInsumos);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    const abrirModalParaCrear = () => {
        setProductoEditando(null);
        setModalAbierto(true);
    };

    const abrirModalParaEditar = (producto) => {
        setProductoEditando(producto);
        setModalAbierto(true);
    };

    const abrirModalReceta = (producto) => {
        setProductoReceta(producto);
        setModalRecetaAbierto(true);
    };

    const cerrarModalReceta = () => {
        setModalRecetaAbierto(false);
        setProductoReceta(null);
        fetchData(); // refresca stock visible en la tabla
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setProductoEditando(null);
    };

    const handleGuardarProducto = (msg) => {
        cerrarModal();
        if (msg) {
            setMensajeExito(msg);
            setTimeout(() => setMensajeExito(''), 3000);
        }
        fetchData(); // Recargar la lista de productos
    };

    const handleEliminarProducto = (producto) => {
        setConfirmDialog({
            title: '¿Eliminar Producto?',
            message: `Esta acción borrará "${producto.nombre}" y sus recetas asociadas de forma permanente.`,
            confirmText: 'Eliminar permanentemente',
            danger: true,
            onConfirm: async () => {
                try {
                    const response = await fetch(getApiUrl(`/productos/${producto.id}`), {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        const errData = await response.json();
                        throw new Error(errData.message || 'Error al eliminar');
                    }
                    setConfirmDialog(null);
                    fetchData();
                    setMensajeExito('Producto eliminado con éxito.');
                    setTimeout(() => setMensajeExito(''), 3000);
                } catch (err) {
                    alert(`Error: ${err.message}`);
                    setConfirmDialog(null);
                }
            }
        });
    };

    if (loading) return <div className="loading">Cargando...</div>;
    if (error) return <div className="error-container"><div className="error">{error}</div></div>;

    return (
        <div className="gestion-catalogo">
            <h2>Gestión de Productos y Recetas</h2>
            <p>Crea, edita o elimina productos y sus recetas asociadas.</p>
            {mensajeExito && <div className="mensaje-exito" style={{ margin: '15px 0' }}>{mensajeExito}</div>}
            
            <button onClick={abrirModalParaCrear} className="btn-crear-nuevo">
                + Crear Nuevo Producto
            </button>

            <table className="catalogo-table">
                <thead>
                    <tr>
                        <th>Nombre</th>
                        <th>Categoría</th>
                        <th>Precio</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {productos.map(p => (
                        <tr key={p.id}>
                            <td>{p.nombre}</td>
                            <td>{p.categoria}</td>
                            <td>S/. {parseFloat(p.precio).toFixed(2)}</td>
                            <td className="acciones-cell">
                                <button onClick={() => abrirModalParaEditar(p)} className="btn-editar">Editar</button>
                                <button onClick={() => abrirModalReceta(p)} className="btn-receta">Receta</button>
                                <button onClick={() => handleEliminarProducto(p)} className="btn-eliminar-prod">Eliminar</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {modalAbierto && (
                <ModalProducto
                    producto={productoEditando}
                    insumosDisponibles={insumos}
                    token={token}
                    onClose={cerrarModal}
                    onSave={handleGuardarProducto}
                />
            )}

            {modalRecetaAbierto && productoReceta && (
                <ModalReceta
                    producto={productoReceta}
                    insumosDisponibles={insumos}
                    token={token}
                    onClose={cerrarModalReceta}
                />
            )}

            {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
        </div>
    );
};

// --- COMBOBOX BUSCADOR DE INSUMOS (reutilizable) ---
const BuscadorInsumo = ({ insumos, value, onChange, placeholder = 'Buscar insumo...' }) => {
    const insumoActual = insumos.find(i => i.id === parseInt(value));
    const [busqueda, setBusqueda] = useState(insumoActual?.nombre || '');
    const [abierto, setAbierto] = useState(false);

    useEffect(() => {
        const found = insumos.find(i => i.id === parseInt(value));
        setBusqueda(found?.nombre || '');
    }, [value]);

    const filtrados = insumos.filter(i =>
        i.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );

    const seleccionar = (insumo) => {
        setBusqueda(insumo.nombre);
        onChange(String(insumo.id));
        setAbierto(false);
    };

    const handleChange = (e) => {
        setBusqueda(e.target.value);
        onChange('');
        setAbierto(true);
    };

    return (
        <div className="buscador-insumo">
            <input
                type="text"
                className="buscador-input"
                placeholder={placeholder}
                value={busqueda}
                onChange={handleChange}
                onFocus={() => setAbierto(true)}
                onBlur={() => setTimeout(() => setAbierto(false), 150)}
            />
            {abierto && filtrados.length > 0 && (
                <ul className="buscador-lista">
                    {filtrados.map(ins => (
                        <li key={ins.id} onMouseDown={() => seleccionar(ins)} className="buscador-item">
                            {ins.nombre}
                        </li>
                    ))}
                </ul>
            )}
            {abierto && busqueda && filtrados.length === 0 && (
                <div className="buscador-vacio">Sin resultados</div>
            )}
        </div>
    );
};

// --- MODAL PARA GESTIONAR RECETA DE UN PRODUCTO ---
const ModalReceta = ({ producto, insumosDisponibles, token, onClose }) => {
    const [receta, setReceta] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nuevoInsumo, setNuevoInsumo] = useState({ id_insumo: '', cantidad_requerida: '' });
    const [error, setError] = useState('');

    const fetchReceta = async () => {
        try {
            const res = await fetch(getApiUrl(`/recetas/recetas/producto/${producto.id}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('No se pudo cargar la receta.');
            const data = await res.json();
            setReceta(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReceta();
    }, [producto.id]);

    const actualizarCantidad = async (item, nuevaCantidad) => {
        const val = parseFloat(nuevaCantidad);
        if (!val || val <= 0) return;
        try {
            await fetch(getApiUrl(`/recetas/recetas/${item.id}`), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ cantidad_requerida: val })
            });
            setReceta(r => r.map(i => i.id === item.id ? { ...i, cantidad_requerida: val } : i));
        } catch (err) {
            setError('Error al actualizar cantidad.');
        }
    };

    const eliminarDeReceta = async (item) => {
        try {
            await fetch(getApiUrl(`/recetas/recetas/${item.id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setReceta(r => r.filter(i => i.id !== item.id));
        } catch (err) {
            setError('Error al eliminar el insumo.');
        }
    };

    const agregarInsumo = async () => {
        const val = parseFloat(nuevoInsumo.cantidad_requerida);
        if (!nuevoInsumo.id_insumo || !val || val <= 0) {
            setError('Selecciona un insumo y una cantidad mayor a 0.');
            return;
        }
        setError('');
        try {
            const res = await fetch(getApiUrl('/recetas/recetas'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ id_producto: producto.id, id_insumo: parseInt(nuevoInsumo.id_insumo), cantidad_requerida: val })
            });
            if (!res.ok) {
                const d = await res.json();
                setError(d.message || 'Error al agregar insumo.');
                return;
            }
            setNuevoInsumo({ id_insumo: '', cantidad_requerida: '' });
            await fetchReceta(); // re-fetch para obtener nombre_insumo y unidad_medida
        } catch (err) {
            setError('Error al agregar insumo.');
        }
    };

    const insumosParaAgregar = insumosDisponibles.filter(
        ins => !receta.some(r => parseInt(r.id_insumo) === ins.id)
    );

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Receta: {producto.nombre}</h2>
                    <button onClick={onClose} className="btn-close-modal">×</button>
                </div>

                {loading ? <p>Cargando receta...</p> : (
                    <>
                        <table className="receta-edit-table">
                            <thead>
                                <tr>
                                    <th>Insumo</th>
                                    <th>Cantidad</th>
                                    <th>Unidad</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {receta.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="modal-hint" style={{ textAlign: 'center', padding: '16px' }}>
                                            Sin insumos asignados. Agrega uno abajo.
                                        </td>
                                    </tr>
                                )}
                                {receta.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.nombre_insumo}</td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0.01"
                                                step="0.01"
                                                defaultValue={parseFloat(item.cantidad_requerida)}
                                                onBlur={(e) => actualizarCantidad(item, e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                className="input-cantidad-receta"
                                            />
                                        </td>
                                        <td>{item.unidad_medida}</td>
                                        <td>
                                            <button
                                                onClick={() => eliminarDeReceta(item)}
                                                className="btn-eliminar"
                                                title="Quitar de la receta"
                                                style={{ padding: '4px 8px' }}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="agregar-insumo-row">
                            <BuscadorInsumo
                                insumos={insumosParaAgregar}
                                value={nuevoInsumo.id_insumo}
                                onChange={(val) => setNuevoInsumo(n => ({ ...n, id_insumo: val }))}
                                placeholder="Buscar insumo..."
                            />
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                placeholder="Cantidad"
                                value={nuevoInsumo.cantidad_requerida}
                                onChange={e => setNuevoInsumo(n => ({ ...n, cantidad_requerida: e.target.value }))}
                                onWheel={(e) => e.target.blur()}
                                className="input-cantidad-receta"
                            />
                            <button onClick={agregarInsumo} className="btn-guardar-prod">+ Agregar</button>
                        </div>

                        {error && <p className="modal-error">{error}</p>}

                        <div className="modal-actions">
                            <button onClick={onClose} className="btn-guardar-prod">Cerrar</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// --- MODAL PARA CREAR/EDITAR PRODUCTO ---
const ModalProducto = ({ producto, insumosDisponibles, token, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        nombre: producto?.nombre || '',
        descripcion: producto?.descripcion || '',
        categoria: producto?.categoria || '',
        precio: producto?.precio || '',
        costo: producto?.costo || '',
        stock_minimo: producto?.stock_minimo || '',
        unidad_medida: producto?.unidad_medida || 'UNI',
        receta: [] // La receta se manejará por separado
    });
    const [receta, setReceta] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRecetaChange = (index, field, value) => {
        const nuevaReceta = [...receta];
        nuevaReceta[index][field] = value;
        setReceta(nuevaReceta);
    };

    const agregarInsumoReceta = () => {
        setReceta([...receta, { id_insumo: '', cantidad_requerida: '' }]);
    };

    const quitarInsumoReceta = (index) => {
        setReceta(receta.filter((_, i) => i !== index));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        // --- VALIDACIÓN DEL FRONTEND ---
        if (receta.some(item => !item.id_insumo || !item.cantidad_requerida || parseFloat(item.cantidad_requerida) <= 0)) {
            alert('Error en la receta: Todos los insumos deben tener una cantidad mayor que cero.');
            return; // Detiene el envío si la validación falla
        }
    
        const payload = { ...formData, receta };
        
        try {
            // Lógica para decidir si es POST (crear) o PUT (editar)
            const esEdicion = !!producto;
            const url = esEdicion ? getApiUrl(`/productos/${producto.id}`) : getApiUrl('/productos');
            const method = esEdicion ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al guardar el producto');
            }
            onSave(`Producto ${esEdicion ? 'actualizado' : 'creado'} con éxito.`);

        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <form onSubmit={handleSubmit}>
                    <h2>{producto ? 'Editar Producto' : 'Crear Nuevo Producto'}</h2>
                    
                    {/* Campos del Producto */}
                    <div className="form-grid">
                        <input name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Nombre del producto" required />
                        <select name="categoria" value={formData.categoria} onChange={handleChange} required className="input-categoria">
                            <option value="">Seleccione Categoría</option>
                            <option value="comida">Comida</option>
                            <option value="piqueos">Piqueos</option>
                            <option value="entrada">Entrada</option>
                            <option value="postre">Postre</option>
                            <option value="bebida">Bebida</option>
                            <option value="trago">Trago</option>
                            <option value="cervezas">Cervezas</option>
                            <option value="botella">Botella</option>
                            <option value="cóctel">Cóctel</option>
                            <option value="promoción">Promoción</option>
                        </select>
                        <input name="precio" type="number" value={formData.precio} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Precio (S/.)" required />
                        <input name="costo" type="number" value={formData.costo} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Costo (S/.)" required />
                        <input name="stock_minimo" type="number" value={formData.stock_minimo} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Stock Mínimo" required />
                        <input name="unidad_medida" value={formData.unidad_medida} onChange={handleChange} placeholder="Unidad de Medida" required />
                    </div>
                    <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} placeholder="Descripción del producto"></textarea>

                    {/* Receta (solo para creación por ahora) */}
                    {!producto && (
                        <div className="receta-section">
                            <h3>Receta del Producto</h3>
                            {receta.map((item, index) => (
                                <div key={index} className="receta-item">
                                    <BuscadorInsumo
                                        insumos={insumosDisponibles}
                                        value={item.id_insumo}
                                        onChange={(val) => handleRecetaChange(index, 'id_insumo', val)}
                                        placeholder="Buscar insumo..."
                                    />
                                    <input type="number" placeholder="Cantidad" value={item.cantidad_requerida} onChange={(e) => handleRecetaChange(index, 'cantidad_requerida', e.target.value)} onWheel={(e) => e.target.blur()} required />
                                    <button type="button" onClick={() => quitarInsumoReceta(index)} className="btn-quitar">-</button>
                                </div>
                            ))}
                            <button type="button" onClick={agregarInsumoReceta} className="btn-agregar-receta">+ Añadir Insumo</button>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancelar">Cancelar</button>
                        <button type="submit" className="btn-guardar-prod">Guardar Producto</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// ====================================================================
// === NUEVO SUB-COMPONENTE PARA EL REPORTE DE COMPRAS ===
// ====================================================================
const ReporteCompras = ({ token }) => {
    const [compras, setCompras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    // 1. AÑADIR ESTADOS PARA LAS FECHAS
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');

    // 2. MODIFICAR LA FUNCIÓN DE BÚSQUEDA PARA INCLUIR LAS FECHAS
    const fetchReporte = async () => {
        try {
            setLoading(true);
            
            // Construir la URL con los query params
            const params = new URLSearchParams();
            if (fechaInicio) params.append('fecha_inicio', fechaInicio);
            if (fechaFin) params.append('fecha_fin', fechaFin);
            
            const response = await fetch(getApiUrl(`/compras/reporte-detallado?${params.toString()}`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('No se pudo cargar el reporte de compras');
            const data = await response.json();
            setCompras(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // 3. LLAMAR A LA BÚSQUEDA INICIAL AL CARGAR EL COMPONENTE
    useEffect(() => {
        fetchReporte();
    }, [token]);

    // 4. MANEJADOR PARA EL BOTÓN DE BÚSQUEDA
    const handleBuscar = () => {
        fetchReporte(); // Simplemente vuelve a llamar a la función con las fechas actuales
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatearFecha = (fechaISO) => {
        if (!fechaISO) return 'N/A';
        const fecha = new Date(fechaISO);
        const opciones = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return fecha.toLocaleDateString('es-ES', opciones);
    };

    if (loading) return <div className="loading">Cargando reporte de compras...</div>;
    if (error) return <div className="error-container"><div className="error">Error: {error}</div></div>;

    return (
        <div className="reporte-compras">
            <h2>Historial de Compras</h2>
            <p>Aquí se listan todas las compras registradas en el sistema. Usa los filtros para acotar tu búsqueda.</p>
            
            {/* 5. AÑADIR LOS FILTROS DE FECHA Y EL BOTÓN A LA INTERFAZ */}
            <div className="filtros-container">
                <div className="filtro-item">
                    <label htmlFor="fecha-inicio">Desde:</label>
                    <input 
                        type="date" 
                        id="fecha-inicio"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                    />
                </div>
                <div className="filtro-item">
                    <label htmlFor="fecha-fin">Hasta:</label>
                    <input 
                        type="date" 
                        id="fecha-fin"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                    />
                </div>
                <button onClick={handleBuscar} className="btn-buscar">Buscar</button>
            </div>
            <table className="compras-reporte-table">
                <thead>
                    <tr>
                        <th className="col-expand"></th>
                        <th>Fecha</th>
                        <th>Proveedor</th>
                        <th>Usuario</th>
                        <th>Total</th>
                        <th>Estado</th>
                    </tr>
                </thead>
                <tbody>
                    {compras.map(compra => (
                        <React.Fragment key={compra.id}>
                            <tr className="main-row" onClick={() => toggleRow(compra.id)}>
                                <td className="col-expand">{expandedRows[compra.id] ? '−' : '+'}</td>
                                <td>{formatearFecha(compra.fecha)}</td>
                                <td>{compra.proveedor}</td>
                                <td>{compra.nombre_usuario}</td>
                                <td>S/. {parseFloat(compra.total).toFixed(2)}</td>
                                <td><span className={`status-badge status-${compra.estado}`}>{compra.estado}</span></td>
                            </tr>
                            {expandedRows[compra.id] && (
                                <tr className="details-row">
                                    <td colSpan="6">
                                        <div className="compra-details">
                                            {compra.observaciones && <p><strong>Observaciones:</strong> {compra.observaciones}</p>}
                                            <table className="detalles-inner-table">
                                                <thead>
                                                    <tr>
                                                        <th>Insumo</th>
                                                        <th>Cantidad</th>
                                                        <th>Precio Unitario</th>
                                                        <th>Subtotal</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {compra.detalles.map(detalle => (
                                                        <tr key={detalle.id_detalle}>
                                                            <td>{detalle.nombre_insumo}</td>
                                                            <td>{detalle.cantidad}</td>
                                                            <td>S/. {parseFloat(detalle.precio_unitario).toFixed(2)}</td>
                                                            <td>S/. {parseFloat(detalle.subtotal).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ====================================================================
// === NUEVO SUB-COMPONENTE PARA EL ESTADO DE PRODUCTOS ===
// ====================================================================
const EstadoProductos = ({ token }) => {
    const [productos, setProductos] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        const fetchEstadoProductos = async () => {
            try {
                setLoading(true);
                const response = await fetch(getApiUrl('/productos/estado-stock'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('No se pudo cargar el estado de los productos');
                const data = await response.json();
                setProductos(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchEstadoProductos();
    }, [token]);

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const productosFiltrados = productos.filter(p => 
        p.nombre.toLowerCase().includes(filtro.toLowerCase())
    );

    if (loading) return <div className="loading">Cargando estado de productos...</div>;
    if (error) return <div className="error-container"><div className="error">Error: {error}</div></div>;

    return (
        <div className="estado-productos">
            <h2>Alerta de Insumos por Producto</h2>
            <p>Revisa qué productos están en riesgo por falta de insumos. Las filas en rojo indican que al menos un insumo está por debajo de su stock mínimo.</p>
            <input 
                type="text"
                placeholder="Buscar producto por nombre..."
                className="search-bar"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
            />
            <table className="productos-estado-table">
                <thead>
                    <tr>
                        <th className="col-expand"></th>
                        <th>Producto</th>
                        <th>Stock Calculado</th>
                        <th>Stock Mínimo</th>
                    </tr>
                </thead>
                <tbody>
                    {productosFiltrados.map(producto => (
                        <React.Fragment key={producto.id}>
                            <tr 
                                className={`main-row ${producto.algun_insumo_en_minimo ? 'critical-stock' : ''}`}
                                onClick={() => toggleRow(producto.id)}
                            >
                                <td className="col-expand">{expandedRows[producto.id] ? '−' : '+'}</td>
                                <td>{producto.nombre}</td>
                                <td>{producto.stock_calculado} {producto.unidad_medida}</td>
                                <td>{producto.stock_minimo_producto} {producto.unidad_medida}</td>
                            </tr>
                            {expandedRows[producto.id] && (
                                <tr className="details-row">
                                    <td colSpan="4">
                                        <div className="insumos-details">
                                            <h4>Insumos Requeridos:</h4>
                                            <ul>
                                                {producto.insumos_receta.map(insumo => (
                                                    <li key={insumo.id_insumo} className={insumo.en_minimo ? 'insumo-low' : ''}>
                                                        <strong>{insumo.nombre_insumo}:</strong>
                                                        <span> Se necesitan {insumo.cantidad_requerida} {insumo.unidad_medida_insumo}.</span>
                                                        <span> Stock actual: {insumo.stock_actual_insumo} (Mínimo: {insumo.stock_minimo_insumo}).</span>
                                                        {insumo.en_minimo && <span className="alerta-minimo">¡STOCK BAJO!</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


// ====================================================================
// === SUB-COMPONENTE GESTION DE INSUMOS (VERSIÓN FINAL CON REPORTES) ===
// ====================================================================
const GestionInsumos = ({ insumos, insumosOriginales, onInsumoChange, onGuardar, onInsumoCreado, idInsumoActualizado }) => {
    const [filtro, setFiltro] = useState('');
    const [modalAbierto, setModalAbierto] = useState(false);
    const { auth } = useAuth();
    const [reporteData, setReporteData] = useState([]);

    // --- ESTADOS PARA EL MODAL DE HISTORIAL ---
    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [historialSeleccionado, setHistorialSeleccionado] = useState([]);
    const [insumoSeleccionado, setInsumoSeleccionado] = useState(null);
    const [cargandoHistorial, setCargandoHistorial] = useState(false);

    // --- ESTADOS PARA EL MODAL DE CONFIRMACIÓN Y NOTAS (AJUSTE) ---
    const [modalConfirmarAbierto, setModalConfirmarAbierto] = useState(false);
    const [insumoParaGuardar, setInsumoParaGuardar] = useState(null);
    const [notaAjuste, setNotaAjuste] = useState('');

    // --- ESTADO PARA MENSAJES DE ÉXITO ---
    const [mensajeExitoLocal, setMensajeExitoLocal] = useState('');

    const mostrarExito = (msg) => {
        setMensajeExitoLocal(msg);
        setTimeout(() => setMensajeExitoLocal(''), 3000);
    };

    // --- ESTADO PARA DIÁLOGOS DE CONFIRMACIÓN (ELIMINAR) ---
    const [confirmDialog, setConfirmDialog] = useState(null);

    const iniciarGuardado = (insumo) => {
        setInsumoParaGuardar(insumo);
        setModalConfirmarAbierto(true);
    };

    const confirmarYGuardar = async () => {
        if (insumoParaGuardar) {
            const success = await onGuardar(insumoParaGuardar, notaAjuste);
            if (success) {
                mostrarExito(`¡${insumoParaGuardar.nombre} actualizado con éxito!`);
            }
        }
        setModalConfirmarAbierto(false);
        setInsumoParaGuardar(null);
        setNotaAjuste('');
    };

    const revertirCambios = (id) => {
        // Buscamos el insumo original del padre
        onInsumoCreado(); // Esto recargará los insumos, pero queremos algo más inmediato
        // Mejor: Si el padre tiene insumosOriginales, podríamos pedirle que restaure este ID.
        // Pero handleInsumoChange solo actualiza 'insumos'.
        // Una forma simple es llamar a "onInsumoCreado" para refrescar todo desde la DB.
        onInsumoCreado();
    };

    const handleEliminarInsumo = (insumo) => {
        setConfirmDialog({
            title: '¿Eliminar insumo?',
            message: `¿Estás seguro de que deseas eliminar "${insumo.nombre}"? Esta acción borrará permanentemente sus existencias e historial de movimientos.`,
            confirmText: 'Eliminar permanentemente',
            danger: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(getApiUrl(`/insumos/${insumo.id}`), {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${auth.token}` }
                    });
                    const data = await res.json();
                    if (res.ok) {
                        setConfirmDialog(null);
                        onInsumoCreado(); 
                    } else {
                        alert(data.message || 'No se pudo eliminar el insumo.');
                        setConfirmDialog(null);
                    }
                } catch (err) {
                    alert('Error de conexión al eliminar insumo.');
                    setConfirmDialog(null);
                }
            }
        });
    };

    const verHistorial = async (insumo) => {
        setInsumoSeleccionado(insumo);
        setModalHistorialAbierto(true);
        setCargandoHistorial(true);
        try {
            const response = await fetch(getApiUrl(`/insumos/${insumo.id}/historial`), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (!response.ok) throw new Error('No se pudo cargar el historial.');
            const data = await response.json();
            setHistorialSeleccionado(data);
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setCargandoHistorial(false);
        }
    };

    const generarReporte = async () => {
        try {
            const response = await fetch(getApiUrl('/insumos/reporte-stock-bajo'), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (!response.ok) throw new Error('No se pudo generar el reporte');
            const data = await response.json();
            setReporteData(data);
            if (data.length > 0) {
                alert(`Reporte generado. Se encontraron ${data.length} insumos con stock bajo.`);
            } else {
                alert('¡Excelente! No se encontraron insumos con stock bajo.');
            }
        } catch (err) {
            alert(`Error al generar el reporte: ${err.message}`);
        }
    };

    const ocultarReporte = () => setReporteData([]);

    const descargarExcel = () => {
        if (reporteData.length === 0) return;
        const worksheet = XLSX.utils.json_to_sheet(reporteData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "StockBajo");
        XLSX.writeFile(workbook, "ReporteStockBajo.xlsx");
    };

    const handlePrint = () => {
        if (!reporteData || reporteData.length === 0) return;

        const ventana = window.open('', '_blank', 'width=800,height=600');
        const fechaActual = new Date().toLocaleString('es-ES');
        const totalEstimado = calcularTotalReporte().toFixed(2);

        ventana.document.write(`
            <html>
            <head>
                <title>Reporte de Necesidad de Compra</title>
                <style>
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #333; line-height: 1.5; }
                    .header { border-bottom: 2px solid #333; margin-bottom: 30px; text-align: center; }
                    .header h2 { margin: 0; color: #000; font-size: 24px; text-transform: uppercase; }
                    .header p { margin: 5px 0; color: #666; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    th, td { border: 1px solid #000; padding: 10px; text-align: left; font-size: 13px; }
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .total-row { background-color: #f2f2f2; font-weight: bold; font-size: 15px; }
                    .footer { margin-top: 40px; text-align: center; font-style: italic; font-size: 12px; color: #777; border-top: 1px dotted #ccc; padding-top: 10px; }
                    @media print {
                        .no-print { display: none; }
                        body { padding: 0; margin: 15mm; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>Reporte de Necesidad de Compra</h2>
                    <p>Restaurante Sistema - Gestión de Inventario</p>
                    <p>Fecha de Generación: ${fechaActual}</p>
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th>Insumo</th>
                            <th class="text-center">Stock Act.</th>
                            <th class="text-center">Stock Mín.</th>
                            <th class="text-center">Cant. a Pedir</th>
                            <th class="text-right">Costo Est.</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${reporteData.map(item => `
                            <tr>
                                <td>${item.nombre}</td>
                                <td class="text-center">${item.stock_actual} ${item.unidad_medida}</td>
                                <td class="text-center">${item.stock_minimo} ${item.unidad_medida}</td>
                                <td class="text-center">${parseFloat(item.cantidad_a_pedir).toFixed(2)} ${item.unidad_medida}</td>
                                <td class="text-right">S/. ${parseFloat(item.costo_estimado_compra).toFixed(2)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr class="total-row">
                            <td colspan="4" class="text-right">TOTAL ESTIMADO DE LA COMPRA:</td>
                            <td class="text-right">S/. ${totalEstimado}</td>
                        </tr>
                    </tfoot>
                </table>

                <div class="footer">
                    <p>Este reporte sugiere cantidades basadas en el stock mínimo configurado para cada insumo.</p>
                </div>

                <script>
                    window.onload = function() {
                        window.print();
                        // Opcional: Cerrar ventana después de imprimir
                        // window.onafterprint = function() { window.close(); };
                    };
                </script>
            </body>
            </html>
        `);
        ventana.document.close();
    };

    const formatearFecha = (fechaISO) => {
        if (!fechaISO) return 'N/A';
        return new Date(fechaISO).toLocaleString('es-ES', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const insumosFiltrados = insumos.filter(insumo => 
        insumo.nombre.toLowerCase().includes(filtro.toLowerCase())
    );

    const calcularTotalReporte = () => {
        return (reporteData || []).reduce((total, item) => {
            const costoItem = parseFloat(item.costo_estimado_compra);
            return total + (isNaN(costoItem) ? 0 : costoItem);
        }, 0);
    };

    return (
        <div className="gestion-insumos">
            {mensajeExitoLocal && (
                <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 4000 }}>
                    <MsgExito msg={mensajeExitoLocal} />
                </div>
            )}
            
            <div className="vista-principal">
                <h2>Ajuste de Inventario</h2>
                <div className="toolbar" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem' }}>
                    <input
                        type="text"
                        placeholder="Buscar insumo..."
                        className="search-bar"
                        style={{ padding: '0.6rem 1rem', borderRadius: 8, border: '1px solid #e2e8f0', width: '100%', maxWidth: 300 }}
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => setModalAbierto(true)} className="btn-crear-nuevo">+ Crear Insumo</button>
                        <button onClick={generarReporte} className="btn-reporte">Reporte Stock Bajo</button>
                    </div>
                </div>

                {reporteData.length > 0 && (
                    <div className="reporte-generado-container-preview" style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: '2rem' }}>
                        <div className="reporte-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0 }}>Previsualización del Reporte</h3>
                            <button onClick={ocultarReporte} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
                        </div>
                        <table className="reporte-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f1f5f9' }}>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Insumo</th>
                                    <th style={{ textAlign: 'center', padding: '0.75rem' }}>Stock</th>
                                    <th style={{ textAlign: 'center', padding: '0.75rem' }}>A Pedir</th>
                                    <th style={{ textAlign: 'right', padding: '0.75rem' }}>Costo Est.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reporteData.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '0.75rem' }}>{item.nombre}</td>
                                        <td style={{ textAlign: 'center', padding: '0.75rem' }}>{item.stock_actual}</td>
                                        <td style={{ textAlign: 'center', padding: '0.75rem' }}>{parseFloat(item.cantidad_a_pedir).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600, color: '#f05b41' }}>S/. {parseFloat(item.costo_estimado_compra).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ fontWeight: 'bold', background: '#f1f5f9' }}>
                                    <td colSpan="3" style={{ padding: '0.75rem', textAlign: 'right' }}>Total Estimado:</td>
                                    <td style={{ textAlign: 'right', padding: '0.75rem', color: '#f05b41' }}>S/. {calcularTotalReporte().toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                        <div className="reporte-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                            <button onClick={descargarExcel} className="btn-excel">Excel</button>
                            <button onClick={handlePrint} className="btn-imprimir">Imprimir</button>
                        </div>
                    </div>
                )}

                <table className="insumos-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Stock</th>
                            <th>Valor (S/.)</th>
                            <th>Mínimo</th>
                            <th>Unidad</th>
                            <th style={{ minWidth: 150 }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {insumosFiltrados.map(insumo => {
                            const esActualizado = insumo.id === idInsumoActualizado;
                            const stockBajo = parseFloat(insumo.stock_actual) <= parseFloat(insumo.stock_minimo);
                            const clasesFila = `${stockBajo ? 'low-stock' : ''} ${esActualizado ? 'fila-actualizada' : ''}`;

                            // --- DETECTAR CAMBIOS ---
                            const original = insumosOriginales?.find(io => io.id === insumo.id);
                            const stockModificado = original && parseFloat(original.stock_actual) !== parseFloat(insumo.stock_actual);
                            const minModificado = original && parseFloat(original.stock_minimo) !== parseFloat(insumo.stock_minimo);
                            const unidadModificada = original && original.unidad_medida !== insumo.unidad_medida;
                            const esDirty = stockModificado || minModificado || unidadModificada;

                            const styleInput = (modificado) => ({
                                width: modificado ? 90 : 85, 
                                padding: '6px 8px',
                                fontWeight: modificado ? 'bold' : 'normal',
                                border: modificado ? '2px solid #f05b41' : '1px solid #e2e8f0',
                                backgroundColor: modificado ? '#fff5f2' : 'white',
                                transition: 'all 0.2s ease'
                            });

                            return (
                                <tr key={insumo.id} className={clasesFila.trim()}>
                                    <td>{insumo.nombre}</td>
                                    <td>
                                        <input 
                                            type="number" 
                                            value={insumo.stock_actual} 
                                            onChange={(e) => onInsumoChange(insumo.id, 'stock_actual', e.target.value)} 
                                            onWheel={(e) => e.target.blur()} 
                                            step="any" 
                                            style={styleInput(stockModificado)} 
                                        />
                                    </td>
                                    <td>S/. {parseFloat(insumo.precio_compra || 0).toFixed(2)}</td>
                                    <td>
                                        <input 
                                            type="number" 
                                            value={insumo.stock_minimo} 
                                            onChange={(e) => onInsumoChange(insumo.id, 'stock_minimo', e.target.value)} 
                                            onWheel={(e) => e.target.blur()} 
                                            step="any" 
                                            style={styleInput(minModificado)} 
                                        />
                                    </td>
                                    <td>
                                        <input 
                                            type="text" 
                                            value={insumo.unidad_medida} 
                                            onChange={(e) => onInsumoChange(insumo.id, 'unidad_medida', e.target.value)} 
                                            style={{ ...styleInput(unidadModificada), width: unidadModificada ? 70 : 65 }} 
                                        />
                                    </td>
                                    <td className="acciones-cell-multi" style={{ display: 'flex', gap: '4px' }}>
                                        <button 
                                            onClick={() => iniciarGuardado(insumo)} 
                                            className="btn-guardar" 
                                            style={{ 
                                                padding: '4px 8px', 
                                                fontSize: '0.8rem',
                                                opacity: esDirty ? 1 : 0.6,
                                                transform: esDirty ? 'scale(1.05)' : 'scale(1)',
                                                boxShadow: esDirty ? '0 0 8px rgba(240, 91, 65, 0.3)' : 'none'
                                            }}
                                            disabled={!esDirty}
                                        >
                                            Guardar
                                        </button>
                                        {esDirty && (
                                            <button 
                                                onClick={() => revertirCambios(insumo.id)} 
                                                className="btn-cancelar-accion" 
                                                style={{ padding: '4px 8px', fontSize: '0.8rem', background: '#718096' }} 
                                                title="Revertir cambios"
                                            >
                                                Undo
                                            </button>
                                        )}
                                        <button onClick={() => verHistorial(insumo)} className="btn-historial" style={{ padding: '4px 8px', fontSize: '0.8rem' }}>Hist.</button>
                                        <button onClick={() => handleEliminarInsumo(insumo)} className="btn-eliminar" style={{ padding: '6px' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6V20a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {modalAbierto && (
                    <ModalCrearInsumo token={auth.token} onClose={() => setModalAbierto(false)} onSave={() => { setModalAbierto(false); onInsumoCreado(); }} />
                )}

                {modalHistorialAbierto && (
                    <ModalHistorial insumo={insumoSeleccionado} historial={historialSeleccionado} loading={cargandoHistorial} onClose={() => setModalHistorialAbierto(false)} />
                )}

                {modalConfirmarAbierto && (
                    <div className="modal-backdrop">
                        <div className="modal-content" style={{ maxWidth: 400 }}>
                            <div className="modal-header">
                                <h3>Ajustar Stock: {insumoParaGuardar?.nombre}</h3>
                                <button onClick={() => setModalConfirmarAbierto(false)} className="btn-close-modal">×</button>
                            </div>
                            <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>Indica el motivo de este cambio manual (merma, corrección, etc.)</p>
                            <textarea
                                className="input-nota-ajuste"
                                style={{ width: '100%', minHeight: 80, padding: '0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem', marginBottom: '1rem' }}
                                placeholder="Escribe aquí una nota..."
                                value={notaAjuste}
                                onChange={(e) => setNotaAjuste(e.target.value)}
                            />
                            <div className="modal-actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button onClick={() => setModalConfirmarAbierto(false)} className="btn-cancelar">Cancelar</button>
                                <button onClick={confirmarYGuardar} className="btn-guardar-prod">Confirmar y Guardar</button>
                            </div>
                        </div>
                    </div>
                )}

                {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
            </div>
        </div>
    );
};


// --- NUEVO SUB-COMPONENTE: MODAL PARA CREAR INSUMO ---
const ModalCrearInsumo = ({ token, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        stock_actual: 0,
        stock_minimo: 0,
        unidad_medida: 'UNI',
        costo_unitario: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre || !formData.unidad_medida) {
            alert('El nombre y la unidad de medida son obligatorios.');
            return;
        }

        try {
            const response = await fetch(getApiUrl('/insumos'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al crear el insumo');
            }

            alert('Insumo creado con éxito.');
            onSave();

        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const stockActual = parseFloat(formData.stock_actual) || 0;
    const costoUnitario = parseFloat(formData.costo_unitario) || 0;
    const valorCalculado = stockActual > 0 && costoUnitario > 0 ? (stockActual * costoUnitario).toFixed(2) : null;

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <form onSubmit={handleSubmit}>
                    <h2>Crear Nuevo Insumo</h2>
                    <div className="form-grid">
                        <input name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Nombre del insumo" required />
                        <input name="unidad_medida" value={formData.unidad_medida} onChange={handleChange} placeholder="Unidad de Medida (ej: UNI, KG, LT)" required />
                        <input name="stock_minimo" type="number" min="0" value={formData.stock_minimo} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Stock Mínimo" required />
                        <input name="stock_actual" type="number" min="0" value={formData.stock_actual} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Stock Inicial (opcional)" />
                        <input name="costo_unitario" type="number" min="0" step="0.01" value={formData.costo_unitario} onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="Costo Unitario S/. (opcional)" />
                    </div>
                    {valorCalculado ? (
                        <p className="form-hint">Valor total inicial: <strong>S/. {valorCalculado}</strong> ({stockActual} × S/.{costoUnitario})</p>
                    ) : (
                        <p className="form-hint">Si ingresa un costo unitario, el valor del inventario se calculará automáticamente. De lo contrario se actualizará con la primera compra.</p>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancelar">Cancelar</button>
                        <button type="submit" className="btn-guardar-prod">Guardar Insumo</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- NUEVO SUB-COMPONENTE: MODAL PARA VER EL HISTORIAL ---
const ModalHistorial = ({ insumo, historial, loading, onClose }) => {
    const [mostrarTodos, setMostrarTodos] = useState(false); // ESTADO PARA VER TODOS

    const formatearFecha = (fechaISO) => {
        if (!fechaISO) return 'N/A';
        return new Date(fechaISO).toLocaleString('es-ES', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    const historialVisible = mostrarTodos ? historial : historial.slice(0, 10);

    return (
        <div className="modal-backdrop">
            <div className="modal-content modal-historial">
                <div className="modal-header">
                    <h2>Historial de Movimientos: {insumo?.nombre}</h2>
                    <button onClick={onClose} className="btn-close-modal">×</button>
                </div>
                {loading ? (
                    <div className="loading">Cargando historial...</div>
                ) : historial.length > 0 ? (
                    <>
                        <table className="historial-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Usuario</th>
                                    <th>Tipo</th>
                                    <th>Stock Anterior</th>
                                    <th>Stock Nuevo</th>
                                    <th>Merma / Ajuste</th>
                                    <th>Notas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historialVisible.map(mov => (
                                    <tr key={mov.id}>
                                        <td>{formatearFecha(mov.fecha_movimiento)}</td>
                                        <td>{mov.nombre_usuario || 'Sistema'}</td>
                                        <td>{mov.tipo_movimiento}</td>
                                        <td>{mov.stock_anterior}</td>
                                        <td>{mov.stock_nuevo}</td>
                                        <td className={mov.cantidad_modificada < 0 ? 'merma' : 'ajuste-positivo'}>
                                            {mov.cantidad_modificada}
                                        </td>
                                        <td className="notas-cell">{mov.notas}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!mostrarTodos && historial.length > 10 && (
                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button 
                                    className="btn-guardar-insumo" 
                                    onClick={() => setMostrarTodos(true)}
                                    style={{ width: 'auto', padding: '8px 20px', fontSize: '14px' }}
                                >
                                    Ver resto del historial ({historial.length - 10} ocultos)
                                </button>
                            </div>
                        )}
                        {mostrarTodos && historial.length > 10 && (
                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button 
                                    className="btn-historial" 
                                    onClick={() => setMostrarTodos(false)}
                                    style={{ width: 'auto', padding: '8px 20px', fontSize: '14px' }}
                                >
                                    Ocultar historial
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="no-data-historial">No hay movimientos de ajuste manual para este insumo.</p>
                )}
            </div>
        </div>
    );
};


// ====================================================================
// === SUB-COMPONENTE REGISTRAR COMPRA (VERSIÓN MEJORADA CON BUSCADOR) ===
// ====================================================================
const RegistrarCompra = ({ insumosList, token, onCompraSuccess }) => {
    const [items, setItems] = useState(() => {
        const saved = localStorage.getItem('draft_compra_items');
        return saved ? JSON.parse(saved) : [{ id_insumo: '', nombre_insumo: '', cantidad: '', precio_unitario: '' }];
    });
    const [proveedor, setProveedor] = useState(() => localStorage.getItem('draft_compra_proveedor') || '');
    const [observaciones, setObservaciones] = useState(() => localStorage.getItem('draft_compra_observaciones') || '');
    const [busqueda, setBusqueda] = useState({}); // Objeto para manejar la búsqueda por fila
    const [sugerencias, setSugerencias] = useState({}); // Objeto para las sugerencias por fila
    const [mensajeExito, setMensajeExito] = useState(''); 
    const [confirmarAbierto, setConfirmarAbierto] = useState(false);

    // --- GUARDADO AUTOMÁTICO (DRAFT) ---
    useEffect(() => {
        localStorage.setItem('draft_compra_items', JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        localStorage.setItem('draft_compra_proveedor', proveedor);
    }, [proveedor]);

    useEffect(() => {
        localStorage.setItem('draft_compra_observaciones', observaciones);
    }, [observaciones]);

    const limpiarDraft = () => {
        localStorage.removeItem('draft_compra_items');
        localStorage.removeItem('draft_compra_proveedor');
        localStorage.removeItem('draft_compra_observaciones');
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    // --- LÓGICA DEL BUSCADOR ---
    const handleBusquedaChange = (index, valor) => {
        const newBusqueda = { ...busqueda, [index]: valor };
        setBusqueda(newBusqueda);

        if (valor.length > 0) {
            const resultados = insumosList.filter(insumo =>
                insumo.nombre.toLowerCase().includes(valor.toLowerCase())
            ).slice(0, 5); // Limitar a 5 sugerencias
            setSugerencias({ ...sugerencias, [index]: resultados });
        } else {
            setSugerencias({ ...sugerencias, [index]: [] });
        }
    };

    const seleccionarInsumo = (index, insumo) => {
        const newItems = [...items];
        newItems[index].id_insumo = insumo.id;
        newItems[index].nombre_insumo = insumo.nombre; // Guardamos el nombre para mostrarlo
        setItems(newItems);

        // Limpiar búsqueda y sugerencias para esa fila
        setBusqueda({ ...busqueda, [index]: '' });
        setSugerencias({ ...sugerencias, [index]: [] });
    };
    // --- FIN LÓGICA DEL BUSCADOR ---

    const agregarItem = () => {
        setItems([...items, { id_insumo: '', nombre_insumo: '', cantidad: '', precio_unitario: '' }]);
    };

    const eliminarItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (items.some(item => !item.id_insumo || !item.cantidad || !item.precio_unitario) || !proveedor) {
            alert('Por favor, completa todos los campos requeridos, incluyendo la selección de un insumo válido para cada fila.');
            return;
        }
        setConfirmarAbierto(true);
    };

    const confirmarYRegistrar = async () => {
        setConfirmarAbierto(false);
        try {
            const response = await fetch(getApiUrl('/compras'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    insumos: items.map(item => ({ 
                        id_insumo: item.id_insumo,
                        cantidad: parseFloat(item.cantidad), 
                        precio_unitario: parseFloat(item.precio_unitario) 
                    })),
                    proveedor,
                    observaciones
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al registrar la compra');
            }
            
            setMensajeExito('Compra registrada con éxito. El stock ha sido actualizado.');
            setTimeout(() => setMensajeExito(''), 5000);
            limpiarDraft();
            setItems([{ id_insumo: '', nombre_insumo: '', cantidad: '', precio_unitario: '' }]);
            setProveedor('');
            setObservaciones('');
            onCompraSuccess();
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <div className="registrar-compra">
            {mensajeExito && (
                <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 4500 }}>
                    <MsgExito msg={mensajeExito} />
                </div>
            )}
            
            {confirmarAbierto && (
                <ConfirmDialog 
                    isOpen={confirmarAbierto}
                    onClose={() => setConfirmarAbierto(false)}
                    onConfirm={confirmarYRegistrar}
                    title="Confirmar Registro de Compra"
                    message={`¿Estás seguro de registrar esta compra del proveedor "${proveedor}" por un total de ${items.length} ítems?`}
                    type="success"
                />
            )}
            <h2>Registrar Nueva Compra de Insumos</h2>
            <form onSubmit={handleSubmit} className="compra-form">
                <div className="form-group-compra">
                    <label>Proveedor</label>
                    <input type="text" value={proveedor} onChange={(e) => setProveedor(e.target.value)} required className="input-proveedor" />
                </div>
                
                <h3 className="insumos-comprados-title">Insumos Comprados</h3>
                <div className="items-container">
                    {items.map((item, index) => (
                        <div key={index} className="compra-item-mejorado">
                            <div className="buscador-insumo-container">
                                <label>Buscar Insumo</label>
                                <input
                                    type="text"
                                    placeholder="Escribe para buscar..."
                                    value={busqueda[index] || ''}
                                    onChange={(e) => handleBusquedaChange(index, e.target.value)}
                                    className="input-busqueda-insumo"
                                    disabled={!!item.id_insumo} // Deshabilitar si ya se seleccionó un insumo
                                />
                                {item.id_insumo && (
                                    <div className="insumo-seleccionado-tag">
                                        {item.nombre_insumo}
                                        <button type="button" onClick={() => handleItemChange(index, 'id_insumo', '')}>×</button>
                                    </div>
                                )}
                                {sugerencias[index]?.length > 0 && (
                                    <ul className="sugerencias-lista">
                                        {sugerencias[index].map(sug => (
                                            <li key={sug.id} onClick={() => seleccionarInsumo(index, sug)}>
                                                {sug.nombre}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="input-group-compra">
                                <label>Cantidad</label>
                                <input 
                                    type="number" 
                                    placeholder="Ej: 10" 
                                    value={item.cantidad} 
                                    onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)} 
                                    onWheel={(e) => e.target.blur()}
                                    required 
                                />
                            </div>
                            <div className="input-group-compra">
                                <label>Precio Unitario (S/.)</label>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="Ej: 2.50" 
                                    value={item.precio_unitario} 
                                    onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)} 
                                    onWheel={(e) => e.target.blur()}
                                    required 
                                />
                            </div>
                            <button type="button" onClick={() => eliminarItem(index)} className="btn-eliminar-item" title="Eliminar fila">-</button>
                        </div>
                    ))}
                </div>
                <button type="button" onClick={agregarItem} className="btn-agregar-item">+ Añadir otro insumo</button>

                <div className="form-group-compra">
                    <label>Observaciones (Opcional)</label>
                    <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)}></textarea>
                </div>

                <button type="submit" className="btn-submit-compra">Registrar Compra</button>
            </form>
        </div>
    );
};

export default Inventario;
